/* useMouseParallax — Bottega-style damped cursor parallax.
   Returns a live { x, y } pixel offset based on cursor distance from
   viewport center. Damped with lerp at rAF cadence (~60fps).

   Disabled on touch devices and when prefers-reduced-motion is set —
   in both cases returns { x: 0, y: 0 } and never installs listeners.

   Usage:
     const { x, y } = useMouseParallax({ strength: 10, damping: 0.08 });
     <img style={{ transform: `translate3d(${x}px,${y}px,0)` }} />
*/
import { useEffect, useRef, useState } from "react";

function canParallax() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (!window.matchMedia("(hover: hover)").matches) return false;
  return true;
}

export default function useMouseParallax({ strength = 10, damping = 0.08, axes = "xy" } = {}) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const rafId = useRef(0);

  useEffect(() => {
    if (!canParallax()) return;

    function onMove(e) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      target.current = {
        x: axes.includes("x") ? dx * strength : 0,
        y: axes.includes("y") ? dy * strength : 0,
      };
    }

    function tick() {
      const cur = current.current;
      const tgt = target.current;
      const nx = cur.x + (tgt.x - cur.x) * damping;
      const ny = cur.y + (tgt.y - cur.y) * damping;
      current.current = { x: nx, y: ny };
      // Update state only when displacement is meaningful — avoid hammering
      // React on micro-deltas once the lerp settles.
      if (Math.abs(nx - cur.x) > 0.02 || Math.abs(ny - cur.y) > 0.02) {
        setPos({ x: nx, y: ny });
      }
      rafId.current = requestAnimationFrame(tick);
    }

    window.addEventListener("mousemove", onMove, { passive: true });
    rafId.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId.current);
    };
  }, [strength, damping, axes]);

  return pos;
}
